import { __read, __spreadArray } from '../../node_modules/tslib/tslib.es6.esm.js';
import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import smoothscroll from 'smoothscroll-polyfill';

var MOBILE_SCREEN = 767;
var MIN_TABS_AVAILABLE_MOBILE = 2;
var LayoutTypes;
(function (LayoutTypes) {
    LayoutTypes["STANDARD"] = "STANDARD";
    LayoutTypes["DROPDOWN"] = "DROPDOWN";
})(LayoutTypes || (LayoutTypes = {}));
/**
 * Checks if nav container (visible part of tabs container with overflow: hidden) width is more than it's content (container with all the tabs) width plus threshold.
 * Threshold is needed cause we don't want to show arrows if nav width is one pixel bigger than all the tabs.
 * If nav container is bigger than it's content + threshold we don't show arrows. In other case we show them.
 * @params navRef {RefObject} - the object that will use as base
 * @returns boolean
 */
var isTabsLeaking = function (navRef) {
    if (!navRef.current)
        return false;
    var mainChild = navRef.current.children[0];
    var buttonsTab = __spreadArray([], __read((mainChild.children || [])), false);
    var selected = buttonsTab.find(function (child) { return child.getAttribute('aria-selected') === 'true'; });
    if (!selected) {
        return navRef.current.clientWidth < mainChild.clientWidth;
    }
    var lastItem = buttonsTab[buttonsTab.length - 1];
    return lastItem.offsetLeft - selected.offsetLeft + lastItem.clientWidth > navRef.current.clientWidth;
};
/**
 * Verify which layout will render for TabHeader
 * Rules: If we have 2 tabs or less in mobile, layout will be 'standard' unless it can be fit in screen, in that case
 * layout will be 'dropdown'
 * If we have more than 2 tabs in mobile, layout will be 'dropdown'
 * @param refStd {RefObject} the object that will use as base of standard layout
 * @param refDropdown {RefObject} the object that will use as base of dropdown layout
 * @param tabs {array} tabs that will render as children to TabHeader
 * @returns object
 */
var useLayout = function (refStd, refDropdown, tabs) {
    /**
     * To know which layout we're render, we need to render 'standard' layout first to get reference,
     * count tabs, verify if is mobile screen, and if we need to change from 'standard' to 'dropdown' in the beginning, the component
     * will blink, to avoid it, we have firstRenderFinished
     */
    var _a = __read(React.useState(LayoutTypes.STANDARD), 2), layout = _a[0], setLayout = _a[1];
    var _b = __read(React.useState(false), 2), firstRenderFinished = _b[0], setFirstRenderFinished = _b[1];
    /**
     * when we change to 'dropdown' style we lose the reference of 'standard' layout,
     * we need to know the width of first two tabs to know when we can go back to
     * 'standard' layout so we use helpRef to grab that value
     **/
    var helpRef = React.useRef(0);
    var isMobileScreen = useMediaQuery({
        maxWidth: MOBILE_SCREEN,
    });
    var minTabsAvailable = React.useMemo(function () { return tabs.length <= MIN_TABS_AVAILABLE_MOBILE; }, [tabs]);
    var handleWindowResize = React.useCallback(function () {
        if (!refStd.current)
            return;
        var tabsLeaking = isTabsLeaking(refStd);
        var allTabsCanBeVisibleInMobile = isMobileScreen && minTabsAvailable && !tabsLeaking;
        setFirstRenderFinished(true);
        if (!isMobileScreen || allTabsCanBeVisibleInMobile) {
            setLayout(LayoutTypes.STANDARD);
            return;
        }
        if (helpRef.current === 0 && minTabsAvailable) {
            var mainChild = refStd.current.children[0];
            var firstTab = mainChild.children[0].clientWidth || 0;
            var secondTab = mainChild.children[1].clientWidth || 0;
            /**
             * calculate width of first two tabs
             **/
            helpRef.current = firstTab + secondTab;
        }
        setLayout(LayoutTypes.DROPDOWN);
    }, [refStd, isMobileScreen, minTabsAvailable]);
    var isLayoutStandard = React.useCallback(function () { return layout === LayoutTypes.STANDARD; }, [layout]);
    React.useEffect(function () {
        if (!refStd.current)
            return;
        handleWindowResize();
        window.addEventListener('resize', handleWindowResize);
        return function () { return window.removeEventListener('resize', handleWindowResize); };
    }, [handleWindowResize, refStd]);
    /**
     * handleWindowResizeDropdown used to know when we can go back to 'standard' layout
     **/
    var handleWindowResizeDropdown = React.useCallback(function () {
        if (!refDropdown.current || layout === LayoutTypes.STANDARD)
            return;
        var tabsCanBeVisible = minTabsAvailable && refDropdown.current.clientWidth > helpRef.current;
        var desktopScreen = !minTabsAvailable && !isMobileScreen;
        if (tabsCanBeVisible || desktopScreen) {
            helpRef.current = 0;
            setLayout(LayoutTypes.STANDARD);
        }
    }, [refDropdown, layout, isMobileScreen, minTabsAvailable]);
    React.useEffect(function () {
        if (!refDropdown.current)
            return;
        window.addEventListener('resize', handleWindowResizeDropdown);
        return function () { return window.removeEventListener('resize', handleWindowResizeDropdown); };
    }, [refDropdown, handleWindowResizeDropdown]);
    return {
        firstRenderFinished: firstRenderFinished,
        isLayoutStandard: isLayoutStandard(),
    };
};
var useHorizontalScroll = function (navRef) {
    var _a = __read(React.useState(false), 2), scrollEnd = _a[0], setScrollEnd = _a[1];
    var _b = __read(React.useState(true), 2), scrollAtStart = _b[0], setScrollAtStart = _b[1];
    var scrollPosition = React.useRef(0);
    var handleWindowResize = React.useCallback(function () {
        if (!navRef.current)
            return;
        var tabsLeaking = isTabsLeaking(navRef);
        setScrollEnd(!tabsLeaking);
        setScrollAtStart(navRef.current.scrollLeft === 0);
    }, [navRef]);
    var handleScroll = React.useCallback(function (e) {
        var target = e.currentTarget;
        // polyfill needed to make TabHeader scroll with {behavior: 'smooth'} on safari/IE
        // eslint-disable-next-line spellcheck/spell-checker
        smoothscroll.polyfill();
        setScrollEnd(target.scrollLeft + target.clientWidth >= target.scrollWidth);
        setScrollAtStart(target.scrollLeft === 0);
        scrollPosition.current = target.scrollLeft;
    }, []);
    var scrollToSelectedTab = React.useCallback(function () {
        var _a;
        var current = navRef.current;
        handleWindowResize();
        if (current && current.scrollTo) {
            var tabs = __spreadArray([], __read((((_a = navRef.current) === null || _a === void 0 ? void 0 : _a.children[0].children) || [])), false);
            var selectedTab = tabs.find(function (child) { return child.getAttribute('aria-selected') === 'true'; });
            if (selectedTab) {
                current.scrollTo({ behavior: 'smooth', left: selectedTab === null || selectedTab === void 0 ? void 0 : selectedTab.offsetLeft });
            }
        }
    }, [navRef, handleWindowResize]);
    var useEffectCleanUp = React.useCallback(function () {
        var _a;
        window.removeEventListener('resize', scrollToSelectedTab);
        (_a = navRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('scroll', handleScroll);
    }, [handleScroll, scrollToSelectedTab, navRef]);
    React.useEffect(function () {
        if (!navRef.current)
            return;
        navRef.current.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', scrollToSelectedTab);
        scrollToSelectedTab();
        return useEffectCleanUp;
    }, [handleScroll, navRef, useEffectCleanUp, scrollToSelectedTab, handleWindowResize]);
    var tabWidth = React.useCallback(function () {
        var current = navRef.current;
        return current ? current.scrollWidth : 0;
    }, [navRef]);
    var distanceToScrollRight = React.useCallback(function () {
        var current = navRef.current;
        return current ? scrollPosition.current + current.clientWidth : 0;
    }, [navRef]);
    var distanceToScrollLeft = React.useCallback(function () {
        var current = navRef.current;
        return current ? scrollPosition.current - current.clientWidth : 0;
    }, [navRef]);
    var scroll = React.useCallback(function () {
        var current = navRef.current;
        if (current && current.scrollTo) {
            current.scrollTo({ behavior: 'smooth', left: scrollPosition.current, top: 0 });
        }
        setScrollEnd(distanceToScrollRight() >= tabWidth());
    }, [navRef, distanceToScrollRight, tabWidth]);
    var scrollRight = React.useCallback(function () {
        var current = navRef.current;
        if (current) {
            if (distanceToScrollRight() <= tabWidth()) {
                scrollPosition.current = distanceToScrollRight();
            }
            else if (distanceToScrollRight() >= tabWidth() - scrollPosition.current) {
                scrollPosition.current = current.scrollWidth;
                setScrollEnd(true);
            }
            setScrollAtStart(false);
        }
        scroll();
    }, [scroll, distanceToScrollRight, navRef, tabWidth]);
    var scrollLeft = React.useCallback(function () {
        var current = navRef.current;
        if (current) {
            if (distanceToScrollLeft() <= 0) {
                scrollPosition.current = 0;
                setScrollAtStart(true);
            }
            else if (distanceToScrollLeft() <= tabWidth()) {
                scrollPosition.current = distanceToScrollLeft();
            }
        }
        scroll();
    }, [scroll, distanceToScrollLeft, navRef, tabWidth]);
    return {
        scrollAtStart: scrollAtStart,
        scrollEnd: scrollEnd,
        scrollLeft: scrollLeft,
        scrollPosition: scrollPosition.current,
        scrollRight: scrollRight,
    };
};

export { LayoutTypes, isTabsLeaking, useHorizontalScroll, useLayout };
//# sourceMappingURL=NvTabsHeader.hooks.esm.js.map
